import React, { useState, useEffect, useCallback } from 'react';
import AgentLayout, { agentFetch } from './AgentLayout';
import { useAgentDirectory, ReferentTag } from '../../utils/useAgentDirectory';

/**
 * AgentLeads — UI MVP pour le lead context (Phase 5 backend).
 *
 * Coexistence avec /agent/clients legacy : route séparée /agent/leads,
 * pas de migration data, pas d'intégration AgentSession.
 *
 * Lead = personne appelée par l'agent, workflow simple :
 *   nouveau → en_cours → qualifié → clôturé
 * (transition vers clôturé possible depuis n'importe quel statut sauf clôturé)
 */

const STATUT_COLORS = {
  nouveau: '#5A85B8',
  en_cours: '#A0763E',
  qualifié: '#7A9E6E',
  clôturé: '#888',
};
const STATUT_LABELS = {
  nouveau: 'Nouveau',
  en_cours: 'En cours',
  qualifié: 'Qualifié',
  clôturé: 'Clôturé',
};

export default function AgentLeads() {
  const [leads, setLeads] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // Visibilité globale : la liste mêle les leads de toute l'équipe —
  // l'annuaire résout assignedAgentId → label lisible (référent).
  const { referentLabel } = useAgentDirectory();

  const fetchLeads = useCallback(async (cursor) => {
    setLoading(true);
    setError(null);
    try {
      const url = cursor
        ? `/api/agent/leads?cursor=${encodeURIComponent(cursor)}&limit=20`
        : `/api/agent/leads?limit=20`;
      const res = await agentFetch(url);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      if (cursor) {
        setLeads((prev) => [...prev, ...data.items]);
      } else {
        setLeads(data.items);
      }
      setNextCursor(data.nextCursor);
    } catch (e) {
      setError(e.message || 'Erreur de chargement des leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads(null);
  }, [fetchLeads]);

  async function handleCreate(input) {
    const res = await agentFetch('/api/agent/leads', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Erreur ${res.status}`);
    }
    setShowCreate(false);
    await fetchLeads(null);
  }

  async function handleChangeStatut(leadId, nouveauStatut) {
    const res = await agentFetch(`/api/agent/leads/${leadId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ nouveauStatut }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Erreur ${res.status}`);
    }
    await fetchLeads(null);
    setSelected(null);
  }

  return (
    <AgentLayout title="Leads">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#5A3C1F', margin: 0 }}>Tous les leads</h2>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            padding: '10px 20px',
            background: '#A0763E',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Nouveau lead
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#FEE', color: '#933', borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading && leads.length === 0 ? (
        <div style={{ padding: 24, color: '#6B5847' }}>Chargement…</div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 24, background: '#FFF8F0', borderRadius: 8, textAlign: 'center', color: '#6B5847' }}>
          Aucun lead pour le moment. Cliquez sur « Nouveau lead » pour commencer.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {leads.map((l) => (
            <div
              key={l.id}
              onClick={() => setSelected(l)}
              style={{
                padding: 16,
                background: '#fff',
                border: '1px solid #E0DDD8',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, color: '#5A3C1F' }}>{l.nom}</div>
                <div style={{ fontSize: 13, color: '#6B5847', marginTop: 2 }}>
                  {l.email || l.telephone || '—'} · source : {l.source} · référent :{' '}
                  <ReferentTag label={referentLabel(l.assignedAgentId)} />
                </div>
              </div>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  background: STATUT_COLORS[l.statut] || '#888',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {STATUT_LABELS[l.statut] || l.statut}
              </span>
              <span style={{ fontSize: 12, color: '#888' }}>
                {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('fr-FR') : '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {nextCursor && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            onClick={() => fetchLeads(nextCursor)}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid #A0763E',
              borderRadius: 6,
              color: '#A0763E',
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Chargement…' : 'Charger plus'}
          </button>
        </div>
      )}

      {showCreate && <CreateLeadModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}

      {selected && (
        <DetailModal
          lead={selected}
          onClose={() => setSelected(null)}
          onChangeStatut={handleChangeStatut}
          referentLabel={referentLabel}
        />
      )}
    </AgentLayout>
  );
}

function CreateLeadModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', source: 'appel_entrant' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!form.nom.trim()) {
      setErr('Nom requis');
      return;
    }
    if (!form.email.trim() && !form.telephone.trim()) {
      setErr('Email ou téléphone requis');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await onCreate({
        nom: form.nom.trim(),
        ...(form.email.trim() && { email: form.email.trim() }),
        ...(form.telephone.trim() && { telephone: form.telephone.trim() }),
        source: form.source,
      });
    } catch (e2) {
      setErr(e2.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        style={{ background: '#fff', padding: 24, borderRadius: 12, minWidth: 400, maxWidth: 500 }}
      >
        <h3 style={{ color: '#5A3C1F', marginTop: 0 }}>Nouveau lead</h3>
        {err && (
          <div style={{ padding: 8, background: '#FEE', color: '#933', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            {err}
          </div>
        )}
        <Field label="Nom *" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
        <Field label="Téléphone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} />
        <label style={{ display: 'block', marginBottom: 12, color: '#5A3C1F', fontSize: 13 }}>
          Source
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            style={inputStyle}
          >
            <option value="appel_entrant">Appel entrant</option>
            <option value="appel_sortant">Appel sortant</option>
            <option value="reco">Recommandation</option>
            <option value="landing">Landing</option>
            <option value="autre">Autre</option>
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            Annuler
          </button>
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Création…' : 'Créer'}
          </button>
        </div>
      </form>
    </div>
  );
}

function DetailModal({ lead, onClose, onChangeStatut, referentLabel }) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const possibleTransitions = {
    nouveau: ['en_cours', 'clôturé'],
    en_cours: ['qualifié', 'clôturé'],
    qualifié: ['clôturé'],
    clôturé: [],
  }[lead.statut] || [];

  async function changeStatut(newStatut) {
    setSubmitting(true);
    setErr(null);
    try {
      await onChangeStatut(lead.id, newStatut);
    } catch (e) {
      setErr(e.message || 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', padding: 24, borderRadius: 12, minWidth: 400, maxWidth: 500 }}
      >
        <h3 style={{ color: '#5A3C1F', marginTop: 0 }}>{lead.nom}</h3>
        <div style={{ color: '#6B5847', fontSize: 14, lineHeight: 1.8 }}>
          <div><strong>ID :</strong> <code style={{ fontSize: 12 }}>{lead.id}</code></div>
          {lead.email && <div><strong>Email :</strong> {lead.email}</div>}
          {lead.telephone && <div><strong>Téléphone :</strong> {lead.telephone}</div>}
          <div><strong>Source :</strong> {lead.source}</div>
          <div>
            <strong>Référent :</strong>{' '}
            <ReferentTag label={referentLabel(lead.assignedAgentId)} />
          </div>
          <div>
            <strong>Statut :</strong>{' '}
            <span
              style={{
                padding: '2px 8px',
                background: STATUT_COLORS[lead.statut] || '#888',
                color: '#fff',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {STATUT_LABELS[lead.statut] || lead.statut}
            </span>
          </div>
          {lead.createdAt && (
            <div><strong>Créé :</strong> {new Date(lead.createdAt).toLocaleString('fr-FR')}</div>
          )}
          {lead.updatedAt && (
            <div><strong>Mis à jour :</strong> {new Date(lead.updatedAt).toLocaleString('fr-FR')}</div>
          )}
        </div>

        {err && (
          <div style={{ padding: 8, background: '#FEE', color: '#933', borderRadius: 6, margin: '12px 0', fontSize: 13 }}>
            {err}
          </div>
        )}

        {possibleTransitions.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid #E0DDD8', paddingTop: 12 }}>
            <div style={{ fontSize: 13, color: '#5A3C1F', marginBottom: 8 }}>Changer le statut :</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {possibleTransitions.map((s) => (
                <button
                  key={s}
                  onClick={() => changeStatut(s)}
                  disabled={submitting}
                  style={{
                    padding: '6px 12px',
                    background: STATUT_COLORS[s],
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: submitting ? 'wait' : 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  → {STATUT_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <button onClick={onClose} style={btnSecondary}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, color: '#5A3C1F', fontSize: 13 }}>
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </label>
  );
}

const inputStyle = {
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  border: '1px solid #D1CCC2',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const btnPrimary = {
  padding: '8px 16px',
  background: '#A0763E',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '8px 16px',
  background: 'transparent',
  color: '#5A3C1F',
  border: '1px solid #D1CCC2',
  borderRadius: 6,
  cursor: 'pointer',
};
